/**
 * Who is signed in, for the whole app.
 *
 * There is no token in JavaScript to inspect: the session lives entirely in
 * httpOnly cookies (`app/auth/routes.py`), so the only way to answer "am I
 * signed in?" is to ask the server. `GET /auth/me` is that question, and its
 * answer is cached here rather than re-asked by every screen.
 *
 * Three states, not two. `loading` exists because "we have not asked yet" is
 * genuinely different from "the answer was no" — collapsing them makes every
 * reload flash the login screen at an already-authenticated user.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { ApiError, api, setSessionExpiredHandler } from '../api/client'
import type { MeResponse } from '../api/types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface AuthValue {
  status: AuthStatus
  user: MeResponse | null
  /** Re-read `/auth/me`. Call after a login or a 2FA verification succeeds. */
  reload: () => Promise<void>
  /** Revoke the refresh family server-side and clear the cookies. */
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

/** Everything that is not a 401/403 is a broken server, not a missing session. */
function isUnauthenticated(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<MeResponse | null>(null)

  const reload = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me')
      setUser(me)
      setStatus('authenticated')
    } catch (err) {
      if (!isUnauthenticated(err)) {
        // A 500 or a dead network is not proof the session ended. Staying
        // anonymous here would sign the user out over a transient blip, so the
        // failure is logged and the previous answer is kept.
        console.error('auth: /auth/me failed', err)
        setStatus((prev) => (prev === 'loading' ? 'anonymous' : prev))
        return
      }
      setUser(null)
      setStatus('anonymous')
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch (err) {
      // The cookies may already be dead; that is the outcome we wanted anyway.
      console.error('auth: /auth/logout failed', err)
    }
    setUser(null)
    setStatus('anonymous')
  }, [])

  useEffect(() => {
    let live = true
    // The API client discovers a dead session on whatever request happened to
    // be in flight. This is the single place that turns that into UI state, so
    // no screen has to handle its own eviction.
    setSessionExpiredHandler(() => {
      if (!live) return
      setUser(null)
      setStatus('anonymous')
    })
    void reload()
    return () => {
      live = false
      setSessionExpiredHandler(null)
    }
  }, [reload])

  const value = useMemo<AuthValue>(
    () => ({ status, user, reload, logout }),
    [status, user, reload, logout],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (ctx === null) throw new Error('useAuth() must be used inside <AuthProvider>')
  return ctx
}
